import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';

const STORAGE_KEY = 'motivo-basic-gate-state';

export interface DemoToggles {
  mode: 'b2b' | 'motivo-basic';
  showCTA: boolean;
  vetting: boolean;
  resources: boolean;
  supervisorsAdded: boolean;
}

const DEFAULT_TOGGLES: DemoToggles = {
  mode: 'b2b',
  showCTA: false,
  vetting: false,
  resources: false,
  supervisorsAdded: false,
};

export interface MotivoBasicGateContextValue extends DemoToggles {
  setToggle: <K extends keyof DemoToggles>(key: K, value: DemoToggles[K]) => void;
  resetDemoState: () => void;

  // Computed values (hardcoded now that onboarding/clinicalInsights toggles are removed)
  demoMode: boolean;
  isUpgraded: boolean;
  isB2BOrigin: boolean;
  isMotivoBasicOrigin: boolean;
  clinicalInsights: boolean;
  onboarding: boolean;
  supervisorsAdded: boolean;
  superviseesAdded: boolean;
  invitationsSent: boolean;
}

const MotivoBasicGateContext = createContext<MotivoBasicGateContextValue | null>(null);

function loadStateFromStorage(): Partial<DemoToggles> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }
  return {};
}

function saveStateToStorage(state: DemoToggles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore localStorage errors
  }
}

function clearStateFromStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

export function MotivoBasicGateProvider({ children }: { children: ReactNode }) {
  const initialState = loadStateFromStorage();

  const [toggles, setToggles] = useState<DemoToggles>({
    ...DEFAULT_TOGGLES,
    ...initialState,
  });

  // Persist state changes to localStorage
  useEffect(() => {
    saveStateToStorage(toggles);
  }, [toggles]);

  const setToggle = useCallback(<K extends keyof DemoToggles>(key: K, value: DemoToggles[K]) => {
    setToggles((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetDemoState = useCallback(() => {
    clearStateFromStorage();
    setToggles({ ...DEFAULT_TOGGLES });
  }, []);

  // Computed values (hardcoded now that onboarding/clinicalInsights toggles are removed)
  const demoMode = true;
  const isUpgraded = true;
  const isB2BOrigin = toggles.mode === 'b2b';
  const isMotivoBasicOrigin = !isB2BOrigin;
  const clinicalInsights = isB2BOrigin;
  const onboarding = false;
  const supervisorsAdded = toggles.supervisorsAdded;
  const superviseesAdded = true;
  const invitationsSent = true;

  const value: MotivoBasicGateContextValue = {
    ...toggles,
    setToggle,
    resetDemoState,
    demoMode,
    isUpgraded,
    isB2BOrigin,
    isMotivoBasicOrigin,
    clinicalInsights,
    onboarding,
    supervisorsAdded,
    superviseesAdded,
    invitationsSent,
  };

  return (
    <MotivoBasicGateContext.Provider value={value}>{children}</MotivoBasicGateContext.Provider>
  );
}

export function useMotivoBasicGate(): MotivoBasicGateContextValue {
  const context = useContext(MotivoBasicGateContext);
  if (!context) {
    throw new Error('useMotivoBasicGate must be used within a MotivoBasicGateProvider');
  }
  return context;
}

// Safe version that returns null if outside provider (for use in shared components like Header)
export function useMotivoBasicGateSafe(): MotivoBasicGateContextValue | null {
  return useContext(MotivoBasicGateContext);
}

export default useMotivoBasicGate;
